import type { PropsWithChildren } from "react";
import { useCallback, useEffect } from "react";

import { DdRum } from "@/crossPlatform/Datadog";
import { usePathname, useSegments } from "@/crossPlatform/expo-router";
import usePushNotificationDeepLinks from "@/hooks/deepLinking/usePushNotificationDeepLinks";
import useAppInstallation from "@/hooks/useAppInstallation";
import useAuth from "@/hooks/useAuth";
import useDeviceNetworkState from "@/hooks/useDeviceNetworkState";
import useIntercomInitializer from "@/hooks/useIntercomInitializer";
import useIntercomLauncherVisibility from "@/hooks/useIntercomLauncherVisibility";
import ColdStartTracker, { ColdStartPhase } from "@/services/ColdStartTracker";
import {
  useAppStartupDispatch,
  useIsRootNavigatorReadySelector,
} from "@/store/reducers/appStartup";
import NetworkStatusBanner from "@/ui/components/common/NetworkStatusBanner";
import ErrorBoundaryProvider from "@/ui/components/errorBoundary/ErrorBoundaryProvider";
import ErrorBoundaryUI from "@/ui/components/errorBoundary/ErrorBoundaryUI";
import useAppStartupSplashScreen from "@/ui/components/navigation/RootNavigator/useAppStartupSplashScreen";
import useBiometricAuth from "@/ui/components/navigation/RootNavigator/useBiometricAuth";
import useInactivityTimeoutHandler from "@/ui/components/navigation/RootNavigator/useInactivityTimeoutHandler";
import useShowUpdateAlert from "@/ui/components/navigation/RootNavigator/useShowUpdateAlert";
import useWebAuthFlow from "@/ui/components/navigation/RootNavigator/useWebAuthFlow";
// Direct import avoids evaluating the @/ui/core barrel, which pulls in all IconDefinitions at startup
import EquipStatusBar from "@/ui/core/EquipStatusBar";

// contents of RootNavigator kept minimal / split out into separate hooks/concerns to prevent over-complexity,
// and to make clear the relationships and dependency ordering of different hooks/concerns
const RootNavigator = ({ children }: PropsWithChildren) => {
  useIntercomInitializer();
  // called in RootNavigator to ensure a singleton instance invoked, not a new instance per useHelpCenter() caller
  useIntercomLauncherVisibility();
  usePushNotificationDeepLinks();
  useShowUpdateAlert();

  const { isAuthenticated } = useAuth();
  const { setIsRootNavigatorReady } = useAppStartupDispatch();
  const isRootNavigatorReady = useIsRootNavigatorReadySelector();
  const { isDeviceOnline } = useDeviceNetworkState();
  const shouldShowOfflineBanner = !isDeviceOnline && isAuthenticated;

  // Datadog RUM view tracking
  const pathname = usePathname();
  const segments = useSegments();
  const viewKey = segments.join("/");
  useEffect(() => {
    void DdRum.startView(viewKey, pathname);
  }, [viewKey, pathname]);

  useAppStartupSplashScreen({ isRootNavigatorReady });

  const onAuthFullyInitialized = useCallback(() => {
    ColdStartTracker.mark(ColdStartPhase.NavigatorReady);
    setIsRootNavigatorReady(true);
  }, [setIsRootNavigatorReady]);

  // each invocation of useAppInstallation in different hooks/components renders an entirely separate hook
  // this leads to race conditions / concurrent execution / potential undoing or redoing of work in that hook
  // therefore we ENSURE that useAppInstallation() is only called ONCE in the entire repo, and it is here
  const { isFirstLaunchSetupDone } = useAppInstallation();
  const { doBiometricAuth, onBiometricAuthFailure } = useBiometricAuth({
    isFirstLaunchSetupDone,
    isRootNavigatorReady,
    onAuthFullyInitialized,
  });
  useInactivityTimeoutHandler({
    doBiometricAuth,
    isFirstLaunchSetupDone,
    onBiometricAuthFailure,
  });
  useWebAuthFlow({ onAuthFullyInitialized });

  return (
    <ErrorBoundaryProvider fallback={<ErrorBoundaryUI />}>
      <EquipStatusBar />
      {shouldShowOfflineBanner && <NetworkStatusBanner />}
      {children}
    </ErrorBoundaryProvider>
  );
};

export default RootNavigator;
